import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment.development';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorHandleService } from '../services/error-handle.service';
import { SalesProgress, SalesProgressResponse } from '../models/sales';

export interface StepModel {
  stepIndex: number;
  title: string;
  subtitle: string;
  status: StepStatus;
  isCompleted: boolean;
  isActive: boolean;
  isEditable: boolean;
  validationError?: string;
}


export type StepStatus = 'pending' | 'in-progress' | 'completed' | 'error';

@Injectable({
  providedIn: 'root'
})

export class ProgressStepBarService {
  private apiUrl = `${environment.APIDEV}`;
  private stepsSubject = new BehaviorSubject<StepModel[]>([]);
  private currentStepIndex = new BehaviorSubject<number>(1);

  constructor(
    private http: HttpClient,
    private errorHandle: ErrorHandleService
  ) { }

  // Get sales detail progress from API
  getSalesDetailProgress(
    id?: number | null,
    startDate?: string,
    endDate?: string
  ): Observable<SalesProgressResponse> {
    let params = new HttpParams();

    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<SalesProgressResponse>(
      `${this.apiUrl}sales_activity/detail/${id}/progress`,
      {
        params,
        ...this.errorHandle._httpOptions()
      }
    ).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  // Load and transform API data to StepModel format
  loadProgressSteps(
    id?: number | null,
    startDate?: string,
    endDate?: string
  ): Observable<StepModel[]> {
    return this.getSalesDetailProgress(id, startDate, endDate).pipe(
      map(response => {
        if (response.success && response.data) {
          const transformedSteps = this.transformApiDataToSteps(response.data);
          this.stepsSubject.next(transformedSteps);
          this.updateCurrentStepIndex(transformedSteps);
          return transformedSteps;
        }
        return [];
      })
    );
  }

  // Transform API data to StepModel format
  private transformApiDataToSteps(apiData: SalesProgress[]): StepModel[] {
    return apiData.map((item, index) => {
      const stepIndex = index + 1;
      const status = this.mapApiStatusToStepStatus(item.status);

      return {
        stepIndex,
        title: item.trans_status,
        subtitle: `Step ${stepIndex}`,
        status,
        isCompleted: status === 'completed',
        isActive: status === 'in-progress',
        isEditable: status === 'completed' || status === 'in-progress'
      };
    });
  }

  // Map API status to StepStatus
  private mapApiStatusToStepStatus(apiStatus: string): StepStatus {
    switch (apiStatus.toLowerCase()) {
      case 'completed':
        return 'completed';
      case 'in progress':
        return 'in-progress';
      case 'pending':
        return 'pending';
      default:
        return 'pending';
    }
  }

  // Update current step index based on active step
  private updateCurrentStepIndex(steps: StepModel[]): void {
    const activeStep = steps.find(step => step.isActive);
    if (activeStep) {
      this.currentStepIndex.next(activeStep.stepIndex);
    }
  }

  // Get all steps as observable
  getSteps(): Observable<StepModel[]> {
    return this.stepsSubject.asObservable();
  }

  // Get current step index as observable
  getCurrentStepIndex(): Observable<number> {
    return this.currentStepIndex.asObservable();
  }

  // Get current step model
  getCurrentStep(): StepModel | undefined {
    return this.stepsSubject.value.find(step =>
      step.stepIndex === this.currentStepIndex.value
    );
  }

  // Get step completion percentage
  getCompletionPercentage(): number {
    const steps = this.stepsSubject.value;
    if (steps.length === 0) return 0;

    const completedSteps = steps.filter(step => step.status === 'completed').length;
    return (completedSteps / steps.length) * 100;
  }

  // Check if can proceed to next step
  canProceedNext(): boolean {
    const currentIndex = this.currentStepIndex.value;
    const steps = this.stepsSubject.value;
    return currentIndex < steps.length;
  }

  // Check if can go back to previous step
  canProceedPrevious(): boolean {
    const currentIndex = this.currentStepIndex.value;
    return currentIndex > 1;
  }

  // Get steps count
  getStepsCount(): number {
    return this.stepsSubject.value.length;
  }
}

